import React, { useCallback, useMemo } from "react";
import {
  AdminPriceTraceTrigger,
  buildOrganizedTourPriceTrace,
} from "../../../features/admin-price-trace";
import { shouldShowOrganizedTourPriceTraceUi } from "../utils/organizedTourTraceUiVisibility";
import "./OrganizedTourPriceTraceTrigger.css";

/**
 * Admin-only TRACE beside organized-tour price (results cards + profile).
 * Compact structured rows; magnifier opens the full modal.
 */
export function OrganizedTourPriceTraceTrigger({
  deal,
  context = "organized_tour",
  ldsOrderRow = null,
  displayIls = null,
  calculateLDSDealFareForRoomsComposition = null,
  variant = "compact",
}) {
  const tracePayload = useMemo(
    () =>
      buildOrganizedTourPriceTrace({
        deal,
        context,
        ldsOrderRow,
        displayIls,
        calculateLDSDealFareForRoomsComposition,
      }),
    [deal, context, ldsOrderRow, displayIls, calculateLDSDealFareForRoomsComposition],
  );

  const onOpenTrace = useCallback(
    (t) => {
      if (!import.meta.env?.DEV) return;
      console.log("[ORG_TOUR_PRICE_TRACE_OPEN]", {
        context,
        entityId: t?.entityId,
        stepCount: t?.steps?.length,
        primary: t?.summary?.primaryRows?.map((r) => r.key),
      });
    },
    [context],
  );

  if (!shouldShowOrganizedTourPriceTraceUi()) return null;

  const primaryRows = Array.isArray(tracePayload?.summary?.primaryRows)
    ? tracePayload.summary.primaryRows
    : [];
  const extraRows = Array.isArray(tracePayload?.summary?.extraRows)
    ? tracePayload.summary.extraRows
    : [];

  const magnifier = (
    <AdminPriceTraceTrigger
      entityType="organized_tour"
      entityId={tracePayload?.entityId}
      entityName={tracePayload?.entityName}
      tracePayload={tracePayload}
      buttonVariant="icon"
      size="small"
      cacheRevision={0}
      onOpenTrace={onOpenTrace}
    />
  );

  if (variant === "icon") {
    return (
      <span className="organized-tour-price-trace-wrap" dir="rtl">
        {magnifier}
      </span>
    );
  }

  return (
    <aside
      className="ot-card-price-trace"
      dir="rtl"
      aria-label="TRACE מחיר לאדמין"
    >
      <div className="ot-card-price-trace__title">
        <span>TRACE מחיר</span>
        {magnifier}
      </div>
      <dl className="ot-card-price-trace__list">
        {primaryRows.map((row) => (
          <div
            key={row.key}
            className="ot-card-price-trace__row ot-card-price-trace__row--primary"
          >
            <dt className="ot-card-price-trace__label">{row.label}</dt>
            <dd className="ot-card-price-trace__value">
              <span>{row.value}</span>
              {row.hint ? (
                <span className="ot-card-price-trace__hint">{row.hint}</span>
              ) : null}
            </dd>
          </div>
        ))}
        {extraRows.map((row) => (
          <div
            key={row.key}
            className="ot-card-price-trace__row ot-card-price-trace__row--extra"
          >
            <dt className="ot-card-price-trace__label">{row.label}</dt>
            <dd className="ot-card-price-trace__value">
              <span>{row.value}</span>
              {row.hint ? (
                <span className="ot-card-price-trace__hint">{row.hint}</span>
              ) : null}
            </dd>
          </div>
        ))}
      </dl>
    </aside>
  );
}
